import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import toast from 'react-hot-toast'

export const getCompanyIskurInfoList = createAsyncThunk(
  'companyIskurInfo/getList',
  async (companyGroupId) => {
    try {
      const response = await axios.get(`/api/companyiskurinfo/${companyGroupId}`)

      const data = response.data.listCompanyIskurInfos || []

      const formattedData = data.map(item => ({
        id: item.id,
        iskurCode: item.iskurCode,
        password: item.password,
        companies: item.companies || [],
        companyNames: item.companies?.map(c => c.name) || [],
        companyIds: item.companies?.map(c => c.id) || [],
        usersInfo: item.usersInfo || [],
        userIds: item.usersInfo?.map(u => u.id) || [],
        companyGroupIds: item.companyGroupIds || [],
        isActive: true,
        createdDate: item.createdDate ? new Date(item.createdDate).toLocaleDateString('tr-TR') : '',
        updatedDate: item.updatedAt ? new Date(item.updatedAt).toLocaleDateString('tr-TR') : ''
      }))

      return {
        data: formattedData,
        total: formattedData.length,
        companyGroupId
      }
    } catch (error) {
      console.error('API Error:', error)
      throw error
    }
  }
)

export const getCompanyIskurInfoById = createAsyncThunk(
  'companyIskurInfo/getById',
  async (id) => {
    try {
      const response = await axios.get(`/api/companyiskurinfo/${id}`)
      return response.data
    } catch (error) {
      throw error
    }
  }
)

export const createCompanyIskurInfo = createAsyncThunk(
  'companyIskurInfo/create',
  async (payload, { dispatch, getState }) => {
    try {
      const authStore = getState().auth

      const requestData = {
        iskurCode: payload.iskurCode,
        password: payload.password,
        companyGroupId: authStore.selectedCompanyGroup,
        companyIds: payload.companyIds,
        userIds: payload.userIds
      }

      const response = await axios.post('/api/companyiskurinfo', requestData)

      toast.success('İŞKUR bilgisi başarıyla eklendi')

      dispatch(getCompanyIskurInfoList(authStore.selectedCompanyGroup))

      return response.data
    } catch (error) {
      toast.error('İŞKUR bilgisi eklenirken hata oluştu')
      throw error
    }
  }
)

export const updateCompanyIskurInfo = createAsyncThunk(
  'companyIskurInfo/update',
  async (payload, { dispatch, getState }) => {
    try {
      const authStore = getState().auth

      const requestData = {
        id: payload.id,
        iskurCode: payload.iskurCode,
        password: payload.password,
        companyIds: payload.companyIds,
        userIds: payload.userIds
      }

      await axios.put('/api/companyiskurinfo', requestData)

      toast.success('İŞKUR bilgisi başarıyla güncellendi')
      dispatch(getCompanyIskurInfoList(authStore.selectedCompanyGroup))

      return payload
    } catch (error) {
      toast.error('İŞKUR bilgisi güncellenirken hata oluştu')
      throw error
    }
  }
)

export const deleteCompanyIskurInfo = createAsyncThunk(
  'companyIskurInfo/delete',
  async (id, { dispatch, getState }) => {
    try {
      await axios.delete(`/api/companyiskurinfo/${id}`)

      toast.success('İŞKUR bilgisi başarıyla silindi')

      const authStore = getState().auth
      dispatch(getCompanyIskurInfoList(authStore.selectedCompanyGroup))

      return id
    } catch (error) {
      toast.error('İŞKUR bilgisi silinirken hata oluştu')
      throw error
    }
  }
)

export const getCompaniesForSelect = createAsyncThunk(
  'companyIskurInfo/getCompaniesForSelect',
  async (companyGroupIds) => {
    try {
      if (!companyGroupIds || companyGroupIds.length === 0) {
        return []
      }
      
      const promises = companyGroupIds.map(groupId => axios.get(`/api/companygroup/${groupId}/companies`))
      const responses = await Promise.all(promises)
      const allCompanies = responses.flatMap(response => response.data)
      
      const uniqueCompanies = allCompanies.filter((company, index, self) => index === self.findIndex(c => c.value === company.value))
      
      return uniqueCompanies
    } catch (error) {
      console.error('Companies fetch error:', error)
      return []
    }
  }
)

export const getUsersForSelect = createAsyncThunk(
  'companyIskurInfo/getUsersForSelect',
  async (companyGroupIds) => {
    try {
      if (!companyGroupIds || companyGroupIds.length === 0) {
        return []
      }

      const promises = companyGroupIds.map(groupId => axios.get(`/api/users?PageSize=1000&page=1&CompanyGroupId=${groupId}`))
      
      const responses = await Promise.all(promises)
      const allUsers = responses.flatMap(response => response.data.users || [])
      const uniqueUsers = allUsers.filter((user, index, self) => index === self.findIndex(u => u.id === user.id))
      
      return uniqueUsers
    } catch (error) {
      console.error('Users fetch error:', error)
      return []
    }
  }
)

// Slice
const companyIskurInfoSlice = createSlice({
  name: 'companyIskurInfo',
  initialState: {
    data: [],
    companies: [],
    users: [],
    selectedItem: null,
    total: 0,
    loading: false,
    createPopupOpen: false,
    updatePopupOpen: false,
    searchTerm: '',
    error: null,
    currentCompanyGroupId: 0
  },
  reducers: {
    toggleCreatePopup: (state, action) => {
      state.createPopupOpen = action.payload
    },
    toggleUpdatePopup: (state, action) => {
      state.updatePopupOpen = action.payload
    },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload
    },
    setSelectedItem: (state, action) => {
      state.selectedItem = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    setCurrentCompanyGroupId: (state, action) => {
      state.currentCompanyGroupId = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      // Get List
      .addCase(getCompanyIskurInfoList.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getCompanyIskurInfoList.fulfilled, (state, action) => {
        state.loading = false
        state.data = action.payload.data
        state.total = action.payload.total
        state.currentCompanyGroupId = action.payload.companyGroupId
      })
      .addCase(getCompanyIskurInfoList.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })

      // Get By Id
      .addCase(getCompanyIskurInfoById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getCompanyIskurInfoById.fulfilled, (state, action) => {
        state.loading = false
        state.selectedItem = action.payload
      })
      .addCase(getCompanyIskurInfoById.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })

      // Create
      .addCase(createCompanyIskurInfo.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createCompanyIskurInfo.fulfilled, (state) => {
        state.loading = false
        state.createPopupOpen = false
      })
      .addCase(createCompanyIskurInfo.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })

      // Update
      .addCase(updateCompanyIskurInfo.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateCompanyIskurInfo.fulfilled, (state) => {
        state.loading = false
        state.updatePopupOpen = false
      })
      .addCase(updateCompanyIskurInfo.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })

      // Delete
      .addCase(deleteCompanyIskurInfo.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteCompanyIskurInfo.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(deleteCompanyIskurInfo.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })

      // Get Companies
      .addCase(getCompaniesForSelect.pending, (state) => {
        state.companies = []
      })
      .addCase(getCompaniesForSelect.fulfilled, (state, action) => {
        state.companies = action.payload || []
      })
      .addCase(getCompaniesForSelect.rejected, (state) => {
        state.companies = []
      })

      // Get Users
      .addCase(getUsersForSelect.pending, (state) => {
        state.users = []
      })
      .addCase(getUsersForSelect.fulfilled, (state, action) => {
        state.users = action.payload || []
      })
      .addCase(getUsersForSelect.rejected, (state) => {
        state.users = []
      })
  }
})

export const {
  toggleCreatePopup,
  toggleUpdatePopup,
  setSearchTerm,
  setSelectedItem,
  clearError,
  setCurrentCompanyGroupId
} = companyIskurInfoSlice.actions

export default companyIskurInfoSlice.reducer